/**
 * USUARIOS — FASE 3.2B-v-a/b/c
 *
 * PaginaUsuarios — lista global con alta inline (usuario + rol inicial,
 * atómico en el backend) y filtro por búsqueda / estado.
 * PaginaUsuario  — ficha: identidad (rol + vínculo), datos básicos
 * (nombre, apellidos, teléfono, email, activo) editables si
 * `usuarios:editar`, cambio de rol dedicado si `usuarios:roles_editar`,
 * histórico de roles de sólo lectura, y sección Seguridad (bloqueo
 * manual, desbloqueo, reset de contraseña) si `usuarios:seguridad_editar`.
 *
 * SEGURIDAD (FASE 3.2B-v-c): SIN CORREO, SIN TOKENS, SIN MFA
 *
 * Bloquear/desbloquear/resetear son las tres únicas acciones de este
 * bloque, y las tres pasan por rutas dedicadas — nunca por el PATCH
 * genérico. El estado se muestra siempre que se puede leer la ficha
 * (`usuarios:leer`); las tres acciones exigen además
 * `usuarios:seguridad_editar`.
 *
 * ROL E IDENTIDAD NO SE EDITAN POR PATCH GENÉRICO (FASE 3.2B-v-b)
 *
 * Cambiar el rol o el vínculo a operario/cliente de un usuario ya creado
 * es la operación dedicada `POST /api/usuarios/:id/cambiar-rol` — el
 * backend ni siquiera la acepta desde el PATCH genérico de datos básicos
 * (ver `usuarios.ts`). El histórico que deja esa operación (quién tenía
 * qué rol, desde cuándo, quién se lo retiró) nunca se borra ni se edita:
 * esta pantalla lo muestra, no lo toca.
 *
 * PLANIFICADOR NO SE OFRECE
 *
 * El selector de rol del alta sólo lista los cinco roles que el backend
 * admite crear (`ROLES_ASIGNABLES`); PLANIFICADOR sigue existiendo como
 * rol legacy, pero no aparece aquí — es una decisión de producto, no
 * ocultar algo que la API fuera a aceptar y esta pantalla estuviera
 * bloqueando por su cuenta.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  api,
  type Cliente,
  FalloApi,
  type HistoricoRol,
  type OperarioResumen,
  type UsuarioDetalle,
  type UsuarioResumen,
} from '../api.ts';
import {
  alternarOrden,
  Avatar,
  CabeceraOrdenable,
  CampoTexto,
  Error as ErrorCaja,
  fechaHora,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { ETIQUETA_ROL } from '../roles.ts';

const ROLES_ASIGNABLES = ['ADMIN', 'CONSULTOR', 'ENCARGADO', 'OPERARIO', 'TECNICO_MANTENIMIENTO', 'CLIENTE'] as const;
/** Roles de persona de plantilla: exigen operario vinculado (su ámbito = sus centros). Espejo del backend (0028/0058). */
const ROLES_CON_OPERARIO: readonly string[] = ['ENCARGADO', 'OPERARIO', 'TECNICO_MANTENIMIENTO'];
type RolAsignable = (typeof ROLES_ASIGNABLES)[number];

function identidadDe(u: UsuarioResumen): string {
  if (u.idOperario !== null) return `${u.operarioCodigo ?? '?'} — ${u.operarioNombre ?? '—'}`;
  if (u.idCliente !== null) return `${u.clienteCodigo ?? '?'} — ${u.clienteRazonSocial ?? '—'}`;
  return '—';
}

/**
 * Mismo texto que `identidadDe()`, pero como enlace real a la ficha
 * vinculada — sólo en la ficha de usuario (FASE 3.3-C4); el listado sigue
 * usando `identidadDe()` en texto plano, sin tocar. Usa los datos que ya
 * trae `UsuarioDetalle`: sin fetch adicional.
 */
function VinculoUsuario({ datos: u }: { datos: UsuarioResumen }): ReactNode {
  if (u.idOperario !== null) {
    return (
      <Link to={`/operarios/${u.idOperario}`}>
        {u.operarioCodigo ?? '?'} — {u.operarioNombre ?? '—'}
      </Link>
    );
  }
  if (u.idCliente !== null) {
    return (
      <Link to={`/clientes/${u.idCliente}`}>
        {u.clienteCodigo ?? '?'} — {u.clienteRazonSocial ?? '—'}
      </Link>
    );
  }
  //  ADMIN, CONSULTOR y PLANIFICADOR (legacy) no llevan vínculo — misma
  //  representación de "sin vínculo" que ya usaba `identidadDe()`.
  return <>—</>;
}

// -------------------------------------------------------------------
//  PaginaUsuarios — lista
// -------------------------------------------------------------------

type FiltroEstado = 'ACTIVOS' | 'INACTIVOS' | 'TODOS';
type FiltroRol = 'TODOS' | RolAsignable;
type FiltroBloqueo = 'TODOS' | 'BLOQUEADOS' | 'NO_BLOQUEADOS';
type FiltroCambioPwd = 'TODOS' | 'REQUERIDO' | 'NO_REQUERIDO';
type OrdenUsuarios = 'nombre' | 'email' | 'activo';
type Dir = 'asc' | 'desc';

/**
 * `estado: 'ACTIVOS'` — no 'TODOS' — porque es lo que la pantalla ya hacía
 * antes de esta fase: excluye inactivos por defecto a propósito (hay
 * cuentas dadas de baja que no interesa ver a diario). Migrar a URL no
 * cambia esa semántica.
 */
const DEFAULTS_USUARIOS = {
  q: '', estado: 'ACTIVOS', rol: 'TODOS', bloqueo: 'TODOS', cambioPwd: 'TODOS', orden: 'nombre', dir: 'asc',
} as const;

export function PaginaUsuarios(): ReactNode {
  const { puede } = useSesion();
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const lista = useListaUrl(DEFAULTS_USUARIOS);

  const q = lista.valor('q');
  const estado = lista.valor('estado') as FiltroEstado;
  const rol = lista.valor('rol') as FiltroRol;
  const bloqueo = lista.valor('bloqueo') as FiltroBloqueo;
  const cambioPwd = lista.valor('cambioPwd') as FiltroCambioPwd;
  const orden = lista.valor('orden') as OrdenUsuarios;
  const dir = lista.valor('dir') as Dir;

  function ordenarPor(columna: OrdenUsuarios): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`.
  const contadorFiltros = [
    estado !== DEFAULTS_USUARIOS.estado,
    rol !== DEFAULTS_USUARIOS.rol,
    bloqueo !== DEFAULTS_USUARIOS.bloqueo,
    cambioPwd !== DEFAULTS_USUARIOS.cambioPwd,
  ].filter(Boolean).length;

  const consulta = new URLSearchParams();
  if (q.trim() !== '') consulta.set('busqueda', q.trim());
  //  `estado` SIEMPRE se manda, sin condición: el default del backend sin
  //  este parámetro es TODOS, pero el default de esta pantalla es ACTIVOS
  //  — omitirlo cuando vale 'ACTIVOS' le pediría a la API justo lo
  //  contrario de lo que hay que pedirle. (Esto es sólo la URL de la
  //  petición a la API, no la de la barra de direcciones: esa la sigue
  //  limpiando `useListaUrl` por su cuenta.)
  consulta.set('estado', estado);
  if (rol !== 'TODOS') consulta.set('rol', rol);
  if (bloqueo !== 'TODOS') consulta.set('bloqueoManual', bloqueo === 'BLOQUEADOS' ? 'true' : 'false');
  if (cambioPwd !== 'TODOS') consulta.set('requiereCambioPwd', cambioPwd === 'REQUERIDO' ? 'true' : 'false');
  if (orden !== DEFAULTS_USUARIOS.orden) consulta.set('orderBy', orden);
  if (dir !== DEFAULTS_USUARIOS.dir) consulta.set('orderDir', dir);

  const usuarios = useDatos(
    () => api.get<{ usuarios: UsuarioResumen[] }>(`/api/usuarios?${consulta.toString()}`),
    [consulta.toString()],
  );

  const idsOperarioUsados = new Set(
    (usuarios.datos?.usuarios ?? []).flatMap((u) => (u.idOperario !== null ? [u.idOperario] : [])),
  );

  return (
    <Shell
      titulo="Usuarios"
      acciones={
        puede('usuarios:crear') && !mostrarNuevo ? (
          <button className="principal" onClick={() => setMostrarNuevo(true)}>
            Nuevo usuario
          </button>
        ) : mostrarNuevo ? (
          <button onClick={() => setMostrarNuevo(false)}>Cancelar</button>
        ) : undefined
      }
    >
      {mostrarNuevo && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Nuevo usuario</h2>
          </div>
          <FormNuevoUsuario
            idsOperarioUsados={idsOperarioUsados}
            alCrear={() => {
              setMostrarNuevo(false);
              usuarios.recargar();
            }}
            alCancelar={() => setMostrarNuevo(false)}
          />
        </div>
      )}

      <div className="panel">
        <FiltrosResponsive
          contador={contadorFiltros}
          busqueda={
            <div className="campo campo-busqueda">
              <label htmlFor="buscar-usuario">Buscar</label>
              <input
                id="buscar-usuario"
                type="text"
                placeholder="Nombre, apellidos, correo, operario o cliente…"
                value={q}
                onChange={(e) => lista.fijar('q', e.target.value)}
              />
            </div>
          }
        >
          <div className="campo">
            <label htmlFor="filtro-estado-usuario">Estado</label>
            <select
              id="filtro-estado-usuario"
              value={estado}
              onChange={(e) => lista.fijar('estado', e.target.value)}
            >
              <option value="ACTIVOS">Activos</option>
              <option value="INACTIVOS">Inactivos</option>
              <option value="TODOS">Todos</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-rol-usuario">Rol</label>
            <select
              id="filtro-rol-usuario"
              value={rol}
              onChange={(e) => lista.fijar('rol', e.target.value)}
            >
              <option value="TODOS">Todos</option>
              {/* `ROLES_ASIGNABLES` ya excluye PLANIFICADOR (legacy, no ofrecido) — es la misma lista que usa el alta. */}
              {ROLES_ASIGNABLES.map((r) => (
                <option key={r} value={r}>{ETIQUETA_ROL[r]}</option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-bloqueo-usuario">Bloqueo manual</label>
            <select
              id="filtro-bloqueo-usuario"
              value={bloqueo}
              onChange={(e) => lista.fijar('bloqueo', e.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="BLOQUEADOS">Bloqueados</option>
              <option value="NO_BLOQUEADOS">No bloqueados</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-cambio-pwd-usuario">Cambio de contraseña</label>
            <select
              id="filtro-cambio-pwd-usuario"
              value={cambioPwd}
              onChange={(e) => lista.fijar('cambioPwd', e.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="REQUERIDO">Requerido</option>
              <option value="NO_REQUERIDO">No requerido</option>
            </select>
          </div>
          {!lista.esPorDefecto && (
            <button
              type="button"
              className="pequeno"
              style={{ alignSelf: 'flex-end' }}
              onClick={lista.limpiar}
            >
              Limpiar filtros
            </button>
          )}
        </FiltrosResponsive>

        <Panel estado={usuarios}>
          {(d) =>
            d.usuarios.length === 0 ? (
              <Vacio titulo={lista.esPorDefecto ? 'No hay usuarios' : 'No hay usuarios que coincidan con los filtros'}>
                {lista.esPorDefecto && puede('usuarios:crear') && (
                  <p className="suave">Crea el primero con el botón «Nuevo usuario».</p>
                )}
              </Vacio>
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <CabeceraOrdenable
                        texto="Nombre"
                        columna="nombre"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('nombre')}
                      />
                      <CabeceraOrdenable
                        texto="Email"
                        columna="email"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('email')}
                      />
                      <th>Rol</th>
                      <th>Identidad / vínculo</th>
                      <CabeceraOrdenable
                        texto="Estado"
                        columna="activo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('activo')}
                      />
                      <th>Último acceso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.usuarios.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <Link to={`/usuarios/${u.id}`} className="con-avatar">
                            <Avatar nombre={`${u.nombre} ${u.apellidos}`} pequeno />
                            <span>{u.apellidos}, {u.nombre}</span>
                          </Link>
                        </td>
                        <td className="suave">{u.email}</td>
                        <td>
                          {u.rol === null ? '—' : (
                            <span className="etiqueta info">{ETIQUETA_ROL[u.rol] ?? u.rol}</span>
                          )}
                        </td>
                        <td className="texto-secundario">{identidadDe(u)}</td>
                        <td>
                          <span className={`etiqueta ${u.activo ? 'exito' : 'neutra'}`}>
                            {u.activo ? 'ACTIVO' : 'INACTIVO'}
                          </span>
                        </td>
                        <td className="suave">{fechaHora(u.ultimoAcceso)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Panel>
      </div>
    </Shell>
  );
}

// -------------------------------------------------------------------
//  PaginaUsuario — ficha
// -------------------------------------------------------------------

export function PaginaUsuario(): ReactNode {
  const { id = '' } = useParams();
  const usuario = useDatos(() => api.get<UsuarioDetalle>(`/api/usuarios/${id}`), [id]);
  const historico = useDatos(() => api.get<{ roles: HistoricoRol[] }>(`/api/usuarios/${id}/roles`), [id]);
  const { puede, usuario: sesion } = useSesion();
  const [mostrarCambiarRol, setMostrarCambiarRol] = useState(false);
  //  Autoprotección (backend): un ADMIN no puede cambiarse su propio rol.
  //  Aquí sólo se oculta el botón — la barrera real es el 409
  //  NO_AUTOCAMBIO_ROL de `POST /api/usuarios/:id/cambiar-rol`.
  const esUnoMismo = sesion?.id === id;

  const nombreCompleto = usuario.datos !== null ? `${usuario.datos.nombre} ${usuario.datos.apellidos}` : '…';

  function alCambiarRol() {
    setMostrarCambiarRol(false);
    usuario.recargar();
    historico.recargar();
  }

  return (
    <Shell
      migas={[
        { texto: 'Usuarios', a: '/usuarios' },
        { texto: nombreCompleto },
      ]}
      titulo={
        <span className="fila">
          <span>{nombreCompleto}</span>
          {usuario.datos !== null && (
            <span className={`etiqueta ${usuario.datos.activo ? 'exito' : 'neutra'}`}>
              {usuario.datos.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
          )}
        </span>
      }
      subtitulo={
        usuario.datos === null ? undefined : (
          <>
            <span>{usuario.datos.email}</span>
            {usuario.datos.rol !== null && (
              <>
                <span aria-hidden="true" className="tenue">·</span>
                <span className="etiqueta info">{ETIQUETA_ROL[usuario.datos.rol] ?? usuario.datos.rol}</span>
              </>
            )}
          </>
        )
      }
    >
      <div className="panel">
        <Panel estado={usuario}>
          {(d) => (
            <div className="panel-cuerpo">
              <div className="fila separa" style={{ alignItems: 'flex-start' }}>
                <IdentidadUsuario datos={d} />
                {puede('usuarios:roles_editar') && !esUnoMismo && !mostrarCambiarRol && (
                  <button className="pequeno" onClick={() => setMostrarCambiarRol(true)}>
                    Cambiar rol
                  </button>
                )}
              </div>
              {mostrarCambiarRol && (
                <>
                  <hr style={{ border: 'none', borderTop: '1px solid var(--borde)', margin: '16px 0' }} />
                  <FormCambiarRol
                    usuario={d}
                    alCambiar={alCambiarRol}
                    alCancelar={() => setMostrarCambiarRol(false)}
                  />
                </>
              )}
              <hr style={{ border: 'none', borderTop: '1px solid var(--borde)', margin: '16px 0' }} />
              {puede('usuarios:editar') ? (
                <FormEditarUsuario datos={d} alEditar={usuario.recargar} />
              ) : (
                <VistaUsuario datos={d} />
              )}
            </div>
          )}
        </Panel>
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Histórico de roles</h2>
        </div>
        <TablaHistoricoRoles estado={historico} />
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Seguridad</h2>
        </div>
        <Panel estado={usuario}>
          {(d) => (
            <PanelSeguridad
              usuario={d}
              puedeGestionar={puede('usuarios:seguridad_editar')}
              alCambiar={usuario.recargar}
            />
          )}
        </Panel>
      </div>
    </Shell>
  );
}

/** Rol y vínculo actuales. Editables sólo a través de «Cambiar rol», nunca en línea. */
function IdentidadUsuario({ datos: d }: { datos: UsuarioDetalle }): ReactNode {
  return (
    <div className="datos">
      <div className="dato">
        <div className="etiqueta-dato">Rol</div>
        <div className="valor-dato">
          {d.rol === null ? '—' : <span className="etiqueta info">{ETIQUETA_ROL[d.rol] ?? d.rol}</span>}
        </div>
      </div>
      <div className="dato">
        <div className="etiqueta-dato">Identidad / vínculo</div>
        <div className="valor-dato">
          <VinculoUsuario datos={d} />
        </div>
      </div>
      {d.requiereCambioPwd && (
        <div className="dato">
          <div className="etiqueta-dato">Contraseña</div>
          <div className="valor-dato">
            <span className="etiqueta aviso">Cambio pendiente</span>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
//  Cambio de rol — FASE 3.2B-v-b
//
//  Misma lógica de identidad condicional que el alta (rol → qué vínculo
//  hace falta), pero sobre un usuario ya existente. El propio operario o
//  cliente que el usuario ya tenía NO se excluye de sí mismo en el
//  buscador (por eso `idsOperarioUsados` descarta la fila del usuario
//  actual): elegir de nuevo su propio vínculo tiene que seguir siendo
//  posible.
// -------------------------------------------------------------------

function FormCambiarRol({
  usuario,
  alCambiar,
  alCancelar,
}: {
  usuario: UsuarioDetalle;
  alCambiar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const rolInicial: RolAsignable = esRolAsignableLocal(usuario.rol) ? usuario.rol : 'CONSULTOR';
  const [rol, setRol] = useState<RolAsignable>(rolInicial);
  const [operarioElegido, setOperarioElegido] = useState<OperarioResumen | null>(null);
  const [clienteElegido, setClienteElegido] = useState<Cliente | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  const necesitaOperario = ROLES_CON_OPERARIO.includes(rol);
  const necesitaCliente = rol === 'CLIENTE';

  const operarios = useDatos(() => api.get<{ operarios: OperarioResumen[] }>('/api/operarios'), []);
  const clientes = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);
  const todosUsuarios = useDatos(() => api.get<{ usuarios: UsuarioResumen[] }>('/api/usuarios'), []);

  const idsOperarioUsados = new Set(
    (todosUsuarios.datos?.usuarios ?? [])
      .filter((u) => u.id !== usuario.id)
      .flatMap((u) => (u.idOperario !== null ? [u.idOperario] : [])),
  );

  function cambiarRolElegido(nuevo: RolAsignable) {
    setRol(nuevo);
    setOperarioElegido(null);
    setClienteElegido(null);
  }

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<UsuarioDetalle>(`/api/usuarios/${usuario.id}/cambiar-rol`, {
        rol,
        idOperario: necesitaOperario ? operarioElegido?.id : null,
        idCliente: necesitaCliente ? clienteElegido?.id : null,
      });
      alCambiar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void confirmar(e); }}>
      <h3 style={{ marginTop: 0 }}>Cambiar rol</h3>
      <div className="campo">
        <label htmlFor="cambiar-rol-select">Nuevo rol</label>
        <select
          id="cambiar-rol-select"
          value={rol}
          onChange={(ev) => cambiarRolElegido(ev.target.value as RolAsignable)}
          disabled={enviando}
        >
          {ROLES_ASIGNABLES.map((r) => (
            <option key={r} value={r}>{ETIQUETA_ROL[r]}</option>
          ))}
        </select>
        {camposMal['rol'] !== undefined && <span className="campo-error">{camposMal['rol']}</span>}
      </div>

      {necesitaOperario && (
        <div className="campo" style={{ marginTop: 8 }}>
          <label>Operario vinculado</label>
          {camposMal['idOperario'] !== undefined && <span className="campo-error">{camposMal['idOperario']}</span>}
          {operarioElegido !== null ? (
            <div style={{ marginTop: 6 }}>
              <span className="chip-centro">
                <Avatar nombre={`${operarioElegido.nombre} ${operarioElegido.apellidos}`} pequeno />
                <span>{operarioElegido.codigo} — {operarioElegido.nombre} {operarioElegido.apellidos}</span>
                <button type="button" aria-label="Quitar operario elegido" onClick={() => setOperarioElegido(null)} disabled={enviando}>×</button>
              </span>
            </div>
          ) : (
            <Panel estado={operarios}>
              {(d) => (
                <SelectorOperarios
                  operarios={d.operarios}
                  excluir={idsOperarioUsados}
                  onSeleccionar={setOperarioElegido}
                  deshabilitado={enviando}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {necesitaCliente && (
        <div className="campo" style={{ marginTop: 8 }}>
          <label>Cliente vinculado</label>
          {camposMal['idCliente'] !== undefined && <span className="campo-error">{camposMal['idCliente']}</span>}
          {clienteElegido !== null ? (
            <div style={{ marginTop: 6 }}>
              <span className="chip-centro">
                <Avatar nombre={clienteElegido.razonSocial} pequeno />
                <span>{clienteElegido.codigo} — {clienteElegido.razonSocial}</span>
                <button type="button" aria-label="Quitar cliente elegido" onClick={() => setClienteElegido(null)} disabled={enviando}>×</button>
              </span>
            </div>
          ) : (
            <Panel estado={clientes}>
              {(d) => (
                <SelectorClientes
                  clientes={d.clientes}
                  onSeleccionar={setClienteElegido}
                  deshabilitado={enviando}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Cambiando…' : 'Confirmar cambio de rol'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function esRolAsignableLocal(rol: string | null): rol is RolAsignable {
  return rol !== null && (ROLES_ASIGNABLES as readonly string[]).includes(rol);
}

// -------------------------------------------------------------------
//  Histórico de roles — sólo lectura, nunca se borra ni se edita.
// -------------------------------------------------------------------

function TablaHistoricoRoles({
  estado,
}: {
  estado: { datos: { roles: HistoricoRol[] } | null; cargando: boolean; error: unknown; recargar: () => void };
}): ReactNode {
  return (
    <Panel estado={estado}>
      {(d) =>
        d.roles.length === 0 ? (
          <Vacio titulo="Sin histórico de roles" />
        ) : (
          <div className="tabla-desliza">
            <table>
              <thead>
                <tr>
                  <th>Rol</th>
                  <th>Asignado</th>
                  <th>Asignado por</th>
                  <th>Retirado</th>
                  <th>Retirado por</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {d.roles.map((r, i) => (
                  <tr key={i}>
                    <td><span className="etiqueta info">{ETIQUETA_ROL[r.rol] ?? r.rol}</span></td>
                    <td className="suave">{fechaHora(r.asignadoEn)}</td>
                    <td className="suave">{r.asignadoPor ?? '—'}</td>
                    <td className="suave">{fechaHora(r.retiradoEn)}</td>
                    <td className="suave">{r.retiradoPor ?? '—'}</td>
                    <td>
                      <span className={`etiqueta ${r.activo ? 'exito' : 'neutra'}`}>
                        {r.activo ? 'ACTIVO' : 'HISTÓRICO'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </Panel>
  );
}

// -------------------------------------------------------------------
//  Seguridad — FASE 3.2B-v-c
//
//  Estado siempre visible con `usuarios:leer` (ya lo exige la ficha
//  entera); las tres acciones (bloquear/desbloquear/resetear) exigen
//  además `usuarios:seguridad_editar`. Ninguna de las tres manda un
//  "por" en el cuerpo: el actor lo pone el backend desde la sesión.
// -------------------------------------------------------------------

function estadoCuenta(d: UsuarioDetalle): { texto: string; clase: string } {
  if (!d.activo) return { texto: 'INACTIVO', clase: 'neutra' };
  if (d.bloqueadoManualmenteEn !== null) return { texto: 'BLOQUEADO (manual)', clase: 'error' };
  if (d.bloqueadoHasta !== null && new Date(d.bloqueadoHasta).getTime() > Date.now()) {
    return { texto: 'BLOQUEADO (temporal)', clase: 'aviso' };
  }
  return { texto: 'ACTIVO', clase: 'exito' };
}

function PanelSeguridad({
  usuario,
  puedeGestionar,
  alCambiar,
}: {
  usuario: UsuarioDetalle;
  puedeGestionar: boolean;
  alCambiar: () => void;
}): ReactNode {
  const [accion, setAccion] = useState<'bloquear' | 'desbloquear' | 'resetear' | null>(null);
  const estado = estadoCuenta(usuario);
  const bloqueoTemporalVigente =
    usuario.bloqueadoHasta !== null && new Date(usuario.bloqueadoHasta).getTime() > Date.now();

  return (
    <div className="panel-cuerpo">
      <div className="datos">
        <div className="dato">
          <div className="etiqueta-dato">Estado de cuenta</div>
          <div className="valor-dato"><span className={`etiqueta ${estado.clase}`}>{estado.texto}</span></div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Último acceso</div>
          <div className="valor-dato">{fechaHora(usuario.ultimoAcceso)}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Intentos fallidos</div>
          <div className="valor-dato">{usuario.intentosFallidos}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Bloqueo temporal hasta</div>
          <div className="valor-dato">{bloqueoTemporalVigente ? fechaHora(usuario.bloqueadoHasta) : '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Bloqueo manual</div>
          <div className="valor-dato">
            {usuario.bloqueadoManualmenteEn === null ? (
              '—'
            ) : (
              <>
                {fechaHora(usuario.bloqueadoManualmenteEn)} · {usuario.bloqueadoManualmentePor ?? '—'}
                <div className="suave pequena">«{usuario.motivoBloqueo}»</div>
              </>
            )}
          </div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Requiere cambio de contraseña</div>
          <div className="valor-dato">
            <span className={`etiqueta ${usuario.requiereCambioPwd ? 'aviso' : 'neutra'}`}>
              {usuario.requiereCambioPwd ? 'Sí' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {puedeGestionar && accion === null && (
        <div className="fila-botones" style={{ marginTop: 16 }}>
          {usuario.bloqueadoManualmenteEn === null ? (
            <button onClick={() => setAccion('bloquear')}>Bloquear</button>
          ) : (
            <button onClick={() => setAccion('desbloquear')}>Desbloquear</button>
          )}
          {bloqueoTemporalVigente && usuario.bloqueadoManualmenteEn === null && (
            <button onClick={() => setAccion('desbloquear')}>Limpiar bloqueo temporal</button>
          )}
          <button onClick={() => setAccion('resetear')}>Resetear contraseña</button>
        </div>
      )}

      {accion === 'bloquear' && (
        <FormBloquear
          usuario={usuario}
          alConfirmar={() => { setAccion(null); alCambiar(); }}
          alCancelar={() => setAccion(null)}
        />
      )}
      {accion === 'desbloquear' && (
        <ConfirmarDesbloqueo
          usuario={usuario}
          alConfirmar={() => { setAccion(null); alCambiar(); }}
          alCancelar={() => setAccion(null)}
        />
      )}
      {accion === 'resetear' && (
        <FormResetearPassword
          usuario={usuario}
          alConfirmar={() => { setAccion(null); alCambiar(); }}
          alCancelar={() => setAccion(null)}
        />
      )}
    </div>
  );
}

function FormBloquear({
  usuario,
  alConfirmar,
  alCancelar,
}: {
  usuario: UsuarioDetalle;
  alConfirmar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    if (motivo.trim() === '') return;
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<UsuarioDetalle>(`/api/usuarios/${usuario.id}/bloquear`, { motivo: motivo.trim() });
      alConfirmar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline nota aviso" style={{ marginTop: 16 }} onSubmit={(e) => { void confirmar(e); }}>
      <p style={{ marginTop: 0 }}>
        ¿Bloquear la cuenta de <strong>{usuario.nombre} {usuario.apellidos}</strong>? No podrá iniciar
        sesión, y si tiene una sesión abierta dejará de funcionar en su siguiente petición.
      </p>
      <CampoTexto label="Motivo del bloqueo" value={motivo} onChange={setMotivo} error={camposMal['motivo']} disabled={enviando} required />
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando || motivo.trim() === ''}>
          {enviando ? 'Bloqueando…' : 'Confirmar bloqueo'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>Cancelar</button>
      </div>
    </form>
  );
}

function ConfirmarDesbloqueo({
  usuario,
  alConfirmar,
  alCancelar,
}: {
  usuario: UsuarioDetalle;
  alConfirmar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function confirmar() {
    setEnviando(true);
    setError(null);
    try {
      await api.post<UsuarioDetalle>(`/api/usuarios/${usuario.id}/desbloquear`);
      alConfirmar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="nota aviso" style={{ marginTop: 16 }}>
      <p style={{ marginTop: 0 }}>
        ¿Desbloquear a <strong>{usuario.nombre} {usuario.apellidos}</strong>? Se quita el bloqueo
        manual (si lo hubiera) y se limpian los intentos fallidos y el bloqueo temporal.
      </p>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button className="principal" disabled={enviando} onClick={() => { void confirmar(); }}>
          {enviando ? 'Desbloqueando…' : 'Confirmar desbloqueo'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>Cancelar</button>
      </div>
    </div>
  );
}

function FormResetearPassword({
  usuario,
  alConfirmar,
  alCancelar,
}: {
  usuario: UsuarioDetalle;
  alConfirmar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [passwordTemporal, setPasswordTemporal] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setCamposMal({});
    if (passwordTemporal !== confirmacion) {
      setCamposMal({ confirmacion: 'No coincide con la contraseña temporal.' });
      return;
    }
    setEnviando(true);
    try {
      await api.post<UsuarioDetalle>(`/api/usuarios/${usuario.id}/resetear-password`, { passwordTemporal });
      alConfirmar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline nota aviso" style={{ marginTop: 16 }} onSubmit={(e) => { void confirmar(e); }}>
      <p style={{ marginTop: 0 }}>
        ¿Resetear la contraseña de <strong>{usuario.nombre} {usuario.apellidos}</strong>? La contraseña
        actual deja de servir de inmediato; deberá cambiarla al entrar con la temporal.
      </p>
      <CampoTexto
        label="Contraseña temporal" type="text" value={passwordTemporal}
        onChange={setPasswordTemporal} error={camposMal['passwordTemporal']} disabled={enviando} required
      />
      <CampoTexto
        label="Confirmar contraseña temporal" type="text" value={confirmacion}
        onChange={setConfirmacion} error={camposMal['confirmacion']} disabled={enviando} required
      />
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Reseteando…' : 'Confirmar reset'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>Cancelar</button>
      </div>
    </form>
  );
}

function VistaUsuario({ datos: d }: { datos: UsuarioDetalle }): ReactNode {
  return (
    <div className="datos">
      <div className="dato">
        <div className="etiqueta-dato">Email</div>
        <div className="valor-dato">{d.email}</div>
      </div>
      <div className="dato">
        <div className="etiqueta-dato">Nombre</div>
        <div className="valor-dato">{d.nombre}</div>
      </div>
      <div className="dato">
        <div className="etiqueta-dato">Apellidos</div>
        <div className="valor-dato">{d.apellidos}</div>
      </div>
      <div className="dato">
        <div className="etiqueta-dato">Teléfono</div>
        <div className="valor-dato">{d.telefono ?? '—'}</div>
      </div>
    </div>
  );
}

function FormEditarUsuario({
  datos,
  alEditar,
}: {
  datos: UsuarioDetalle;
  alEditar: () => void;
}): ReactNode {
  const [email, setEmail] = useState(datos.email);
  const [nombre, setNombre] = useState(datos.nombre);
  const [apellidos, setApellidos] = useState(datos.apellidos);
  const [telefono, setTelefono] = useState(datos.telefono ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});
  const [guardadoOk, setGuardadoOk] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    setGuardadoOk(false);
    try {
      await api.patch<UsuarioDetalle>(`/api/usuarios/${datos.id}`, {
        email,
        nombre,
        apellidos,
        telefono: telefono.trim() || null,
        activo,
      });
      setGuardadoOk(true);
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto
          label="Email"
          type="email"
          value={email}
          onChange={(v) => { setEmail(v); setGuardadoOk(false); }}
          error={camposMal['email']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Nombre"
          value={nombre}
          onChange={(v) => { setNombre(v); setGuardadoOk(false); }}
          error={camposMal['nombre']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Apellidos"
          value={apellidos}
          onChange={(v) => { setApellidos(v); setGuardadoOk(false); }}
          error={camposMal['apellidos']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Teléfono"
          value={telefono}
          onChange={(v) => { setTelefono(v); setGuardadoOk(false); }}
          error={camposMal['telefono']}
          disabled={enviando}
        />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none', fontSize: 14, display: 'flex', alignItems: 'center',
            gap: 8, cursor: 'pointer', color: 'var(--texto)', fontWeight: 'normal', marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(ev) => { setActivo(ev.target.checked); setGuardadoOk(false); }}
            disabled={enviando}
          />
          Usuario activo
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      {guardadoOk && (
        <div className="nota exito" role="status">
          Cambios guardados. El usuario está <strong>{activo ? 'ACTIVO' : 'INACTIVO'}</strong>.
        </div>
      )}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Alta
// -------------------------------------------------------------------

const LONGITUD_MINIMA_PASSWORD = 12;
const CARACTERES_PASSWORD = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789-_';

function generarPasswordTemporal(): string {
  const azar = new Uint32Array(16);
  crypto.getRandomValues(azar);
  return Array.from(azar, (n) => CARACTERES_PASSWORD[n % CARACTERES_PASSWORD.length]).join('');
}

function FormNuevoUsuario({
  idsOperarioUsados,
  alCrear,
  alCancelar,
}: {
  idsOperarioUsados: Set<string>;
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [telefono, setTelefono] = useState('');
  const [rol, setRol] = useState<RolAsignable>('CONSULTOR');
  const [operarioElegido, setOperarioElegido] = useState<OperarioResumen | null>(null);
  const [clienteElegido, setClienteElegido] = useState<Cliente | null>(null);
  const [passwordTemporal, setPasswordTemporal] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});
  const [creado, setCreado] = useState<{ email: string; password: string } | null>(null);

  const necesitaOperario = ROLES_CON_OPERARIO.includes(rol);
  const necesitaCliente = rol === 'CLIENTE';

  const operarios = useDatos(
    () => api.get<{ operarios: OperarioResumen[] }>('/api/operarios'),
    [],
  );
  const clientes = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);

  function cambiarRol(nuevo: RolAsignable) {
    setRol(nuevo);
    setOperarioElegido(null);
    setClienteElegido(null);
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<UsuarioDetalle>('/api/usuarios', {
        email,
        nombre,
        apellidos,
        telefono: telefono.trim() || null,
        rol,
        idOperario: necesitaOperario ? operarioElegido?.id : null,
        idCliente: necesitaCliente ? clienteElegido?.id : null,
        passwordTemporal,
      });
      setCreado({ email, password: passwordTemporal });
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  if (creado !== null) {
    return (
      <div className="panel-cuerpo">
        <div className="nota exito" role="status">
          <p>
            Usuario <strong>{creado.email}</strong> creado. Contraseña temporal:{' '}
            <strong className="mono">{creado.password}</strong>
          </p>
          <p className="pequena" style={{ marginBottom: 0 }}>
            Comunícasela por un canal seguro: no se puede volver a consultar desde aquí. Deberá
            cambiarla al entrar por primera vez.
          </p>
        </div>
        <div className="fila-botones">
          <button className="principal" onClick={alCrear}>
            Aceptar
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto label="Email" type="email" value={email} onChange={setEmail} error={camposMal['email']} disabled={enviando} required />
        <CampoTexto label="Nombre" value={nombre} onChange={setNombre} error={camposMal['nombre']} disabled={enviando} required />
        <CampoTexto label="Apellidos" value={apellidos} onChange={setApellidos} error={camposMal['apellidos']} disabled={enviando} required />
        <CampoTexto label="Teléfono" value={telefono} onChange={setTelefono} error={camposMal['telefono']} disabled={enviando} />
        <div className="campo">
          <label htmlFor="nuevo-usuario-rol">Rol</label>
          <select
            id="nuevo-usuario-rol"
            value={rol}
            onChange={(ev) => cambiarRol(ev.target.value as RolAsignable)}
            disabled={enviando}
          >
            {ROLES_ASIGNABLES.map((r) => (
              <option key={r} value={r}>{ETIQUETA_ROL[r]}</option>
            ))}
          </select>
          {camposMal['rol'] !== undefined && <span className="campo-error">{camposMal['rol']}</span>}
        </div>
      </div>

      {necesitaOperario && (
        <div className="campo" style={{ marginTop: 8 }}>
          <label>Operario vinculado</label>
          {camposMal['idOperario'] !== undefined && <span className="campo-error">{camposMal['idOperario']}</span>}
          {operarioElegido !== null ? (
            <div style={{ marginTop: 6 }}>
              <span className="chip-centro">
                <Avatar nombre={`${operarioElegido.nombre} ${operarioElegido.apellidos}`} pequeno />
                <span>{operarioElegido.codigo} — {operarioElegido.nombre} {operarioElegido.apellidos}</span>
                <button type="button" aria-label="Quitar operario elegido" onClick={() => setOperarioElegido(null)} disabled={enviando}>×</button>
              </span>
            </div>
          ) : (
            <Panel estado={operarios}>
              {(d) => (
                <SelectorOperarios
                  operarios={d.operarios}
                  excluir={idsOperarioUsados}
                  onSeleccionar={setOperarioElegido}
                  deshabilitado={enviando}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {necesitaCliente && (
        <div className="campo" style={{ marginTop: 8 }}>
          <label>Cliente vinculado</label>
          {camposMal['idCliente'] !== undefined && <span className="campo-error">{camposMal['idCliente']}</span>}
          {clienteElegido !== null ? (
            <div style={{ marginTop: 6 }}>
              <span className="chip-centro">
                <Avatar nombre={clienteElegido.razonSocial} pequeno />
                <span>{clienteElegido.codigo} — {clienteElegido.razonSocial}</span>
                <button type="button" aria-label="Quitar cliente elegido" onClick={() => setClienteElegido(null)} disabled={enviando}>×</button>
              </span>
            </div>
          ) : (
            <Panel estado={clientes}>
              {(d) => (
                <SelectorClientes
                  clientes={d.clientes}
                  onSeleccionar={setClienteElegido}
                  deshabilitado={enviando}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      <div className="campo" style={{ marginTop: 8 }}>
        <label htmlFor="nuevo-usuario-password">Contraseña temporal</label>
        <div className="fila" style={{ gap: 8 }}>
          <input
            id="nuevo-usuario-password"
            type="text"
            value={passwordTemporal}
            onChange={(e) => setPasswordTemporal(e.target.value)}
            disabled={enviando}
            className="mono"
            style={{ flex: 1 }}
          />
          <button type="button" onClick={() => setPasswordTemporal(generarPasswordTemporal())} disabled={enviando}>
            Generar
          </button>
        </div>
        <p className="tenue pequena" style={{ marginTop: 4, marginBottom: 0 }}>
          Al menos {LONGITUD_MINIMA_PASSWORD} caracteres. El usuario deberá cambiarla al entrar por
          primera vez.
        </p>
        {camposMal['passwordTemporal'] !== undefined && <span className="campo-error">{camposMal['passwordTemporal']}</span>}
      </div>

      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Creando…' : 'Crear usuario'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Buscadores de operario / cliente para el alta
// -------------------------------------------------------------------

const LIMITE_RESULTADOS = 30;

function SelectorOperarios({
  operarios,
  excluir,
  onSeleccionar,
  deshabilitado,
}: {
  operarios: OperarioResumen[];
  excluir: Set<string>;
  onSeleccionar: (o: OperarioResumen) => void;
  deshabilitado?: boolean;
}): ReactNode {
  const [busqueda, setBusqueda] = useState('');
  const disponibles = operarios.filter((o) => !excluir.has(o.id));
  const t = busqueda.trim().toLowerCase();
  const filtrados = t === ''
    ? disponibles
    : disponibles.filter(
        (o) =>
          o.codigo.toLowerCase().includes(t) ||
          o.nombre.toLowerCase().includes(t) ||
          o.apellidos.toLowerCase().includes(t),
      );
  const mostrados = filtrados.slice(0, LIMITE_RESULTADOS);

  return (
    <div className="selector-centros">
      <input
        type="text"
        placeholder="Nombre, apellidos o código de operario…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        disabled={deshabilitado}
        aria-label="Buscar operario"
      />
      {disponibles.length === 0 ? (
        <p className="suave pequena">No queda ningún operario disponible: todos ya tienen usuario.</p>
      ) : mostrados.length === 0 ? (
        <p className="suave pequena">Sin resultados para «{busqueda}».</p>
      ) : (
        <ul className="lista-resultados">
          {mostrados.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="resultado-centro"
                onClick={() => onSeleccionar(o)}
                disabled={deshabilitado}
              >
                <Avatar nombre={`${o.nombre} ${o.apellidos}`} pequeno />
                <span className="resultado-texto">
                  <strong>{o.apellidos}, {o.nombre} <span className="mono suave">{o.codigo}</span></strong>
                  <span className="suave">{o.tipo}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SelectorClientes({
  clientes,
  onSeleccionar,
  deshabilitado,
}: {
  clientes: Cliente[];
  onSeleccionar: (c: Cliente) => void;
  deshabilitado?: boolean;
}): ReactNode {
  const [busqueda, setBusqueda] = useState('');
  const t = busqueda.trim().toLowerCase();
  const filtrados = t === ''
    ? clientes
    : clientes.filter(
        (c) => c.codigo.toLowerCase().includes(t) || c.razonSocial.toLowerCase().includes(t),
      );
  const mostrados = filtrados.slice(0, LIMITE_RESULTADOS);

  return (
    <div className="selector-centros">
      <input
        type="text"
        placeholder="Nombre o código de cliente…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        disabled={deshabilitado}
        aria-label="Buscar cliente"
      />
      {mostrados.length === 0 ? (
        <p className="suave pequena">Sin resultados para «{busqueda}».</p>
      ) : (
        <ul className="lista-resultados">
          {mostrados.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="resultado-centro"
                onClick={() => onSeleccionar(c)}
                disabled={deshabilitado}
              >
                <Avatar nombre={c.razonSocial} pequeno />
                <span className="resultado-texto">
                  <strong>{c.razonSocial} <span className="mono suave">{c.codigo}</span></strong>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
